import { parse } from "date-fns";
import { getNowLuxonDateTime, toLuxonDateTimeFromUnixTime } from "./luxonUtil";
import { isNumber } from "./regExpUtils";

class URLParams {
  searchParams: URLSearchParams;

  constructor(path: string) {
    this.searchParams = new URLSearchParams(path);
  }

  /**
   * 引数で指定されたキーに対応する値を取得する。
   * @param key パラメーターのキー文字列。
   * @param type 取得するパラメータの型
   */
  getParam(key: string, type: string) {
    switch(type) {
      case 'string':
        return this.getStringParam(key);
      case 'number':
        return this.getNumberParam(key);
      case 'boolean':
        return this.getBooleanParam(key);
      case 'date':
        return this.getDateParam(key);
      case 'unixtime':
        return this.getDateFromUnixTimeMilisecondsParam(key);
      default:
        return null;
    }
  }

  checkParams(keys: string[]): boolean {
    const result = keys.find((k)=>{
      return this.searchParams.get(k) === null;
    })
    return result === undefined;
  }

  /**
   * 引数で指定されたキーに対応する値を取得する。
   * @param key パラメーターのキー文字列。
   */
  getStringParam(key: string): string | null {
    // nullとなる場合は空文字を返す。
    return (this.searchParams.get(key) !== null) ? this.searchParams.get(key) : '';
  }

  /**
   * 引数で指定されたキーに対応する値を数値型として取得する。
   * @param key パラメーターのキー文字列。
   */
  getNumberParam(key: string): number {
    // nullとなる場合は0を返す。
    const param = (this.searchParams.get(key) !== null) ? this.searchParams.get(key) : '0';
    return Number.parseInt((param === null) ? '0' : param);
  }

  /**
   * 引数で指定されたキーに対応する値をfloat型の浮動小数点数値型として取得する。
   * @param key パラメーターのキー文字列。
   */
  getFloatParam(key: string): number {
    // nullとなる場合は0を返す。
    const param = (this.searchParams.get(key) !== null) ? this.searchParams.get(key) : '0';
    return Number((param === null) ? '0' : param);
  }

  /**
   * 引数で指定されたキーに対応する値をBoolean型として取得する。
   * @param key パラメーターのキー文字列。
   */
  getBooleanParam(key: string): boolean {
    // nullとなる場合はfalseを返す。
    const param = (this.searchParams.get(key) !== null) ? this.searchParams.get(key) : '';
    return param === 'true' ? true : false;
  }

  /**
   * 引数で指定されたキーに対応する値（yyyyMMdd形式の値）をDate型として取得する。
   * @param key パラメーターのキー文字列。
   * @param defaultDate パラメーターを解析できなかった時のデフォルト日付
   */
  getDateParam(key: string, defaultDate: Date = new Date()): Date {
    // 空文字やnullとなる場合は現在日時を返す。
    const param = (this.searchParams.get(key) !== null) ? this.searchParams.get(key) : '';
    return (param && param !== null) ? parse(param, 'yyyyMMdd', defaultDate) : defaultDate;
  }

  /**
   * 引数で指定されたキーに対応する値（UNIXTIME Miliseconds）をDate型として取得する。
   * @param key パラメーターのキー文字列。
   * @param defaultDate パラメーターを解析できなかった時のデフォルト日付
   */
  getDateFromUnixTimeMilisecondsParam(key: string, defaultDate: Date = new Date()): Date {
    // 空文字やnullとなる場合は現在日時を返す。
    const param = (this.searchParams.get(key) !== null) ? this.searchParams.get(key) : '';
    return (param && param !== null) ? new Date(Number(param)) : defaultDate;
  }

  /**
   * 引数で指定されたキーに対応する値（UNIXTIME Miliseconds）を Luxon.DateTime 型として取得する。
   * @param key パラメーターのキー文字列。
   * @param defaultDate パラメーターを解析できなかった時のデフォルト日付
   */
  getLuxonDateTimeUnixTimeMilisecondsParam(key: string, defaultDate: luxon.DateTime = getNowLuxonDateTime()): luxon.DateTime {
    // 空文字やnullとなる場合は現在日時を返す。
    const param = (this.searchParams.get(key) !== null) ? this.searchParams.get(key) : null;
    // UnixTime Milisecondsで来るので、UnixTimeに変換する
    return (param && param !== null && isNumber(param)) ? toLuxonDateTimeFromUnixTime(Number(param)/1000) : defaultDate;
  }

  /**
   * 引数で指定されたキーに対応する値が存在するかを確認する。
   * @param key パラメーターのキー文字列。
   */
  hasParam(key: string): boolean {
    return this.searchParams.has(key);
  }
}

export default URLParams;